import { createBrowserRouter, RouterProvider, Navigate, useNavigate } from 'react-router-dom';
import { PatientRouteErrorBoundary } from './PatientRouteErrorBoundary';
import { patientRoutes } from '../modules/patient/routes';
import OnboardingGate from '../modules/patient/auth/OnboardingGate';


import SplashScreen from '../modules/patient/pages/entry/SplashScreen';
import EntryChoiceScreen from '../modules/patient/pages/entry/EntryChoiceScreen';
import LoginScreen from '../modules/patient/pages/entry/LoginScreen';
import EmailLoginScreen from '../modules/patient/pages/entry/EmailLoginScreen';
import ForgotPasswordScreen from '../modules/patient/pages/entry/ForgotPasswordScreen';
import GoogleLoginPlaceholderScreen from '../modules/patient/pages/entry/GoogleLoginPlaceholderScreen';
import ApprovalPendingScreen from '../modules/patient/pages/entry/ApprovalPendingScreen';
import VerificationUpdateRequiredScreen from '../modules/patient/pages/entry/VerificationUpdateRequiredScreen';
import OnboardingScreen from '../modules/patient/pages/entry/OnboardingScreen';
import PatRegStartScreen from '../modules/patient/pages/entry/PatRegStartScreen';
import PatRegBasicScreen from '../modules/patient/pages/entry/PatRegBasicScreen';
import PatRegContactScreen from '../modules/patient/pages/entry/PatRegContactScreen';
import PatRegOTPScreen from '../modules/patient/pages/entry/PatRegOTPScreen';
import PatRegHealthScreen from '../modules/patient/pages/entry/PatRegHealthScreen';
import PatRegEmergencyScreen from '../modules/patient/pages/entry/PatRegEmergencyScreen';
import PatRegAbhaScreen from '../modules/patient/pages/entry/PatRegAbhaScreen';
import PatRegConsentScreen from '../modules/patient/pages/entry/PatRegConsentScreen';
import PatRegNHCIDScreen from '../modules/patient/pages/entry/PatRegNHCIDScreen';
import PatRegCardScreen from '../modules/patient/pages/entry/PatRegCardScreen';
import PatRegSuccessScreen from '../modules/patient/pages/entry/PatRegSuccessScreen';
import ApkFullFlowPreview from '../modules/patient/pages/ApkFullFlowPreview';

function PatientRoleGate() {
  return <Navigate to="/patient/splash" replace />;
}

// SOURCE-OF-TRUTH LOCK:
// /patient and /patient/home → approved PatientHome (via OnboardingGate + routes.tsx index)
// /patient/apk-full-flow → preview-only, not production home
// /patient/entry → entry/login, not full APK preview
const router = createBrowserRouter([
  /* APK Full Flow Preview — dev-only visual walkthrough. In production it
     redirects to entry: an unguarded interactive dashboard route must never
     ship to real users. */
  {
    path: '/patient/apk-full-flow',
    element: import.meta.env.DEV ? <ApkFullFlowPreview /> : <Navigate to="/patient/entry" replace />,
  },

  /* Entry screens — no auth guard, no footer */
  { path: '/patient/splash', element: <SplashScreen /> },
  { path: '/patient/entry', element: <EntryChoiceScreen /> },

  /* Login routes */
  { path: '/patient/login/phone', element: <LoginScreen /> },
  { path: '/patient/login/email', element: <EmailLoginScreen /> },
  { path: '/patient/login/forgot', element: <ForgotPasswordScreen /> },
  { path: '/patient/login/google', element: <GoogleLoginPlaceholderScreen /> },

  /* Approval status screens */
  { path: '/patient/approval-pending', element: <ApprovalPendingScreen /> },
  { path: '/patient/verification-update-required', element: <VerificationUpdateRequiredScreen /> },

  /* Registration flow */
  { path: '/patient/onboarding', element: <OnboardingScreen /> },
  { path: '/patient/register/start', element: <PatRegStartScreen /> },
  { path: '/patient/register/basic', element: <PatRegBasicScreen /> },
  { path: '/patient/register/contact', element: <PatRegContactScreen /> },
  { path: '/patient/register/otp', element: <PatRegOTPScreen /> },
  { path: '/patient/register/health', element: <PatRegHealthScreen /> },
  { path: '/patient/register/emergency', element: <PatRegEmergencyScreen /> },
  { path: '/patient/register/abha', element: <PatRegAbhaScreen /> },
  { path: '/patient/register/consent', element: <PatRegConsentScreen /> },
  { path: '/patient/register/nhc-id', element: <PatRegNHCIDScreen /> },
  { path: '/patient/register/card', element: <PatRegCardScreen /> },
  { path: '/patient/register/success', element: <PatRegSuccessScreen /> },
  { path: '/patient/register/approval-pending', element: <ApprovalPendingScreen /> },

  /* Legacy login route — redirect to the real patient entry flow.
     (The former dev role-switcher LoginPage was removed: it allowed
     unauthenticated selection of arbitrary roles incl. SUPER_ADMIN.) */
  { path: '/login', element: <Navigate to="/patient/entry" replace /> },

  /* Dashboard — protected by OnboardingGate (includes auth check) */
  {
    path: `/${patientRoutes.path}`,
    element: (
      <OnboardingGate>
        {patientRoutes.element}
      </OnboardingGate>
    ),
    errorElement: <PatientRouteErrorBoundary />,
    children: patientRoutes.children,
  },
  { path: '/', element: <PatientRoleGate /> },
  { path: '*', element: <PatientRouteErrorBoundary /> },
]);

export function PatientRouter() {
  return <RouterProvider router={router} />;
}
